import { defineNuxtRouteMiddleware, getUserInfo, isPikPakClient, promisifyBridge, useRoute, useRouter } from '#imports'
import { getExtensionId, loginInitParams } from '@/config'
import { useUserInfo } from '@/store/userInfo'
import { CustomEventType } from '@/types/enum'
import { requestLoginAuthCode } from '@/utils/jsBridge'

// 使用授权code进行登录
export const loginByCode = async (authCode: string) => {
  await window.loginUI?.signOut()
  await (await import('@/composables/useLogin')).handleAndMountLoginPopup()
  await window.loginUI?.login.auth.grantToken({
    code: authCode,
    grant_type: 'authorization_code',
  })
}

function getAuthCodeFromExtension() {
  return new Promise(resolve => {
    const timeout = setTimeout(() => {
      resolve('')
    }, 5000)
    if (window.chrome?.runtime?.sendMessage) {
      window.chrome.runtime.sendMessage(getExtensionId(), { type: 'LOGIN_REQUEST' }).then(code => {
        clearTimeout(timeout)
        resolve(code)
      })
    }
  })
}

function logoutOnceHandle() {
  if (import.meta.server) return
  const router = useRouter()
  const route = useRoute()
  const { from_source: fromSource } = route.query || {}
  if (fromSource && fromSource === 'pp_browser_extension') {
    router.replace({
      query: {
        ...(route.query || {}),
        from_source: undefined,
      },
    })
  }

  window.removeEventListener(CustomEventType.LOGOUT_SUCCESS, logoutOnceHandle)
}

export default defineNuxtRouteMiddleware(async to => {
  if (import.meta.server) return
  const { code, bridge_auth: bridgeAuth, from_source: fromSource, port, scope, state } = to.query

  const userInfoStore = useUserInfo()

  // 如果是app内嵌页
  if (isPikPakClient(window.navigator.userAgent) && bridgeAuth === '1') {
    // webview中页面会重定向，导致两次自动登录，所以此处如果已经有登录态，则不再重新登录
    const isLogin = await window.loginUI?.isLoginStateValid()
    if (isLogin) return
    const { authCode } = (await promisifyBridge(requestLoginAuthCode, {})) || {}
    authCode && (await loginByCode(authCode))
    userInfoStore.refresh()
    return
  }

  // 判断是否从三方登录回来带的code
  const isOAuthCallback = Boolean(code && state)
  if (code && !isOAuthCallback) {
    try {
      if (!scope) {
        await loginByCode(code as string)
        userInfoStore.refresh()
        return
      }
      if (isPikPakClient(window.navigator.userAgent)) {
        const clientUserInfo = await promisifyBridge(getUserInfo, {})
        const localInfoKey = `credentials_${loginInitParams.loginInitOptions.clientId}`
        const localInfoString = window.localStorage.getItem(localInfoKey) || '{}'
        const localInfo = JSON.parse(localInfoString)
        if (localInfo?.sub) {
          const expiresTime = new Date(localInfo.expires_at)
          const isSameUser = localInfo.sub === clientUserInfo.userID
          const isTokenAvailable = Date.now() < expiresTime.getTime()
          if (isSameUser && isTokenAvailable) return true
        }
      }
    } catch (err) {
      console.error('invalid auth code!', err)
      return
    }
  }

  // 如果是从插件跳转过来
  if (fromSource && fromSource === 'pp_browser_extension') {
    getAuthCodeFromExtension().then(authCode => {
      authCode &&
        loginByCode(authCode as string).then(() => {
          useUserInfo().refresh()
          window.dispatchEvent(new CustomEvent(CustomEventType.AFTER_LOGIN))
          window.addEventListener(CustomEventType.LOGOUT_SUCCESS, logoutOnceHandle)
        })
    })
  }

  // 如果是从pc应用跳转到web页面（携带from_source=pc_client参数）
  // 尝试请求http://127.0.0.1:${port}/windows/client-auth-code 获取code
  if (fromSource && fromSource === 'pc_client' && port) {
    const servePort = parseInt(port as string, 16)
    try {
      const f = await fetch(`http://127.0.0.1:${servePort}/windows/client-auth-code`, {
        method: 'post',
      })
      const authCode = f.headers.get('X-Auth-Code')
      if (authCode) {
        await loginByCode(authCode as string)
        useUserInfo().refresh()
        window.dispatchEvent(new CustomEvent(CustomEventType.AFTER_LOGIN))
        return
      }
    } catch (err) {
      console.error('sync auth code error! ', err)
    }
  }
  userInfoStore.refresh()
})
